// @flow

import * as React from 'react';
import { Box } from 'react-system';
import scrollbarSize from 'dom-helpers/scrollbarSize';
import { Filter } from '../../../controls/Filter';
import { useLocale } from '../../../hooks/locale';
import { createDescriptor } from '../../../controls/router';

import type { AggregatesListingsFilters_root } from '../__generated__/AggregatesListingsFilters_root.graphql.js';
import { NumberMonospace } from './NumberMonospace';
import {
  pageDescriptor,
  surfaceFilterDescriptor,
  livingSurfaceFilterDescriptor,
  landSurfaceFilterDescriptor,
} from './sharedDescriptors';
import { FilterMinMax } from './FilterMinMax';

const formatOptions = {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
};

const combinedFilterDescriptor = createDescriptor(
  surfaceFilterDescriptor,
  pageDescriptor,
);

type Props = {|
  root: AggregatesListingsFilters_root,
  inline?: boolean,
  shouldFlush: boolean,
|};

export const SurfaceFilter = ({
  root,
  inline,
  shouldFlush,
}: Props): React.Node => {
  // const currency = 'CHF';
  const empty = livingSurfaceFilterDescriptor.useIsEmpty();
  const { t, numberLocale } = useLocale();
  const [open, setOpen] = React.useState(false);

  const [
    configFilters,
    setConfigFilters,
    flushConfigFilters,
  ] = combinedFilterDescriptor.useState();

  const flush = () => {
    setConfigFilters({ page: 0 });
    if (shouldFlush) {
      flushConfigFilters();
    }
  };

  const livingSurface = (
    <FilterMinMax
      autoFocus={true}
      desktopDropdownVariant="inline"
      title={t('livingSurface')}
      value={{
        min: configFilters.livingSurface_gte,
        max: configFilters.livingSurface_lte,
      }}
      onChange={({ min, max }) =>
        setConfigFilters({
          livingSurface_gte: min,
          livingSurface_lte: max,
        })
      }
      onClear={() => {
        setConfigFilters({
          livingSurface_gte: null,
          livingSurface_lte: null,
        });
        flush();
      }}
      onFlush={flush}
      onDone={() => setOpen(false)}
      suggestionsOptions={{
        format: v => numberLocale.format(Math.round(v), formatOptions),
        formatPostfix: 'm²',
        constraints: {
          minValue: root.aggregatesListingInfo.minLivingSurface,
          maxValue: root.aggregatesListingInfo.maxLivingSurface,
          ranges: {
            min: [
              { from: 30, to: 100, step: 10 },
              { from: 100, to: 200, step: 20 },
              { from: 200, to: Infinity, step: 50 },
            ],
            max: [
              { from: 30, to: 100, step: 10 },
              { from: 100, to: 200, step: 20 },
              { from: 200, to: Infinity, step: 50 },
            ],
          },
        },
        anyTitle: t('any'),
      }}
    />
  );

  const dialogTitle = t('surfaceFilter');

  const getLabel = (from, to) => {
    if (from != null && to == null) {
      return t('metersOver', {
        value: from,
      });
    } else if (from == null && to != null) {
      return t('metersUnder', {
        value: to,
      });
    } else if (from != null && to != null) {
      return t('metersBetween', {
        from,
        to,
      });
    }

    return dialogTitle;
  };

  const label = getLabel(
    configFilters.livingSurface_gte,
    configFilters.livingSurface_lte,
  );

  // const sliders = [livingSurface];

  const PADDING_X = 16;
  return inline === true ? (
    livingSurface
  ) : (
    <Filter
      label={<NumberMonospace>{label}</NumberMonospace>}
      empty={empty}
      open={open}
      onOpenChange={open => setOpen(open)}
      onReset={() => {
        setConfigFilters(surfaceFilterDescriptor.defaults);
        flush();
      }}
    >
      <Box
        width={[`calc(100vw - ${PADDING_X * 2 + scrollbarSize()}px )`, '320px']}
        p={3}
      >
        {livingSurface}
      </Box>
    </Filter>
  );
};

export const LandSurface = ({
  root,
  shouldFlush,
  inline,
}: Props): React.Node => {
  const { t, numberLocale } = useLocale();
  const [open, setOpen] = React.useState(false);

  const [
    configFilters,
    setConfigFilters,
    flushConfigFilters,
  ] = combinedFilterDescriptor.useState();
  const empty = landSurfaceFilterDescriptor.useIsEmpty();

  const flush = () => {
    setConfigFilters({ page: 0 });
    if (shouldFlush) {
      flushConfigFilters();
    }
  };

  const inlineElement = (
    <FilterMinMax
      title={t('landSurface')}
      value={{
        min: configFilters.landSurface_gte,
        max: configFilters.landSurface_lte,
      }}
      onChange={({ min, max }) =>
        setConfigFilters({
          landSurface_gte: min,
          landSurface_lte: max,
        })
      }
      onClear={() => {
        setConfigFilters({
          landSurface_gte: null,
          landSurface_lte: null,
        });
        flush();
      }}
      onFlush={flush}
      onDone={() => setOpen(false)}
      suggestionsOptions={{
        format: v => numberLocale.format(Math.round(v), formatOptions),
        formatPostfix: 'm²',
        constraints: {
          minValue: root.aggregatesListingInfo.minLandSurface,
          maxValue: root.aggregatesListingInfo.maxLandSurface,
          ranges: {
            min: [
              { from: 200, to: 1000, step: 200 },
              { from: 1000, to: Infinity, step: 1000 },
            ],
            max: [
              { from: 200, to: 1000, step: 200 },
              { from: 1000, to: Infinity, step: 1000 },
            ],
          },
        },
      }}
    />
  );

  const PADDING_X = 16;
  return inline === true ? (
    inlineElement
  ) : (
    <Filter
      label={<NumberMonospace>{t('landSurface')}</NumberMonospace>}
      empty={empty}
      open={open}
      onReset={() => {
        setConfigFilters(landSurfaceFilterDescriptor.defaults);
        flush();
      }}
    >
      <Box
        width={[`calc(100vw - ${PADDING_X * 2 + scrollbarSize()}px )`, '320px']}
        css={{
          maxHeight: 'calc(100vh - 260px)', // 260 = top bar + some free space
        }}
        p={3}
      >
        {inlineElement}
      </Box>
    </Filter>
  );
};
